import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Search,
  Filter,
  CheckSquare,
  Square,
  Trash2,
  X,
  RefreshCw,
  AlertTriangle,
  DollarSign,
  Eye,
  Calendar
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminJobsManagement = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobs, setSelectedJobs] = useState(new Set());
  const [allSelected, setAllSelected] = useState(false);
  const [showBulkConfirmation, setShowBulkConfirmation] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    payment_status: 'all',
    search: ''
  });

  // Redirect if not admin
  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      toast.error('Admin access required');
      return;
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchJobs();
    }
  }, [user, filters]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/payments`);
      let filteredJobs = response.data;

      // Apply filters
      if (filters.status !== 'all') {
        filteredJobs = filteredJobs.filter(job => job.status === filters.status);
      }
      
      if (filters.payment_status !== 'all') {
        filteredJobs = filteredJobs.filter(job => 
          (job.payment_status || 'unfunded') === filters.payment_status
        );
      }

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredJobs = filteredJobs.filter(job => 
          job.title?.toLowerCase().includes(searchLower) ||
          job.location?.toLowerCase().includes(searchLower) ||
          job.originator_business?.toLowerCase().includes(searchLower) ||
          job.id.toLowerCase().includes(searchLower)
        );
      }

      setJobs(filteredJobs);
      // Clear selection when jobs change
      setSelectedJobs(new Set());
      setAllSelected(false);
      
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedJobs(new Set());
      setAllSelected(false);
    } else {
      setSelectedJobs(new Set(jobs.map(job => job.id)));
      setAllSelected(true);
    }
  };

  const handleSelectJob = (jobId) => {
    const newSelected = new Set(selectedJobs);
    if (newSelected.has(jobId)) {
      newSelected.delete(jobId);
    } else {
      newSelected.add(jobId);
    }
    setSelectedJobs(newSelected);
    setAllSelected(newSelected.size === jobs.length && jobs.length > 0);
  };

  const getValidActions = () => {
    if (selectedJobs.size === 0) return [];
    
    const selectedJobData = jobs.filter(job => selectedJobs.has(job.id));
    const actions = [];
    
    // Cancel Jobs - valid for posted/passed_back unassigned jobs
    const cancelableJobs = selectedJobData.filter(job => 
      ['posted', 'passed_back'].includes(job.status) && !job.accepted_by
    );
    if (cancelableJobs.length > 0) {
      actions.push({
        id: 'cancel',
        label: `Cancel Jobs (${cancelableJobs.length})`,
        icon: X,
        color: 'text-red-600',
        validCount: cancelableJobs.length
      });
    }
    
    // Refund & Cancel - valid for funded, unassigned jobs
    const refundableJobs = selectedJobData.filter(job => 
      ['posted', 'passed_back'].includes(job.status) && 
      job.payment_status === 'funded' && 
      !job.accepted_by
    );
    if (refundableJobs.length > 0) {
      actions.push({
        id: 'refund_cancel',
        label: `Refund & Cancel (${refundableJobs.length})`,
        icon: DollarSign,
        color: 'text-orange-600',
        validCount: refundableJobs.length
      });
    }
    
    // Delete Drafts - valid for draft status only
    const draftJobs = selectedJobData.filter(job => job.status === 'draft');
    if (draftJobs.length > 0) {
      actions.push({
        id: 'delete_drafts',
        label: `Delete Drafts (${draftJobs.length})`,
        icon: Trash2,
        color: 'text-gray-600',
        validCount: draftJobs.length
      });
    }
    
    return actions;
  };

  const handleBulkAction = async (action) => {
    const jobIds = Array.from(selectedJobs);
    const idempotencyKey = `${action}_${jobIds.join('_')}_${Date.now()}`;
    
    try {
      setBulkLoading(true);
      
      const response = await axios.post(`${API}/admin/jobs/bulk`, {
        action: action,
        job_ids: jobIds
      }, {
        headers: {
          'Idempotency-Key': idempotencyKey
        }
      });
      
      setBulkResults(response.data);
      
      // Show summary toast
      const { succeeded, skipped, failed } = response.data.summary;
      if (failed > 0) {
        toast.error(`Bulk action completed: ${succeeded} succeeded, ${skipped} skipped, ${failed} failed. View details for more info.`);
      } else if (skipped > 0) {
        toast.warning(`Bulk action completed: ${succeeded} succeeded, ${skipped} skipped. View details for more info.`);
      } else {
        toast.success(`Bulk action completed successfully: ${succeeded} jobs processed.`);
      }
      
      // Refresh jobs and clear selection
      await fetchJobs();
      setSelectedJobs(new Set());
      setAllSelected(false);
      
    } catch (error) {
      console.error('Bulk action failed:', error);
      toast.error('Bulk action failed. Please try again.');
    } finally {
      setBulkLoading(false);
      setShowBulkConfirmation(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      posted: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Live' },
      accepted: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Assigned' },
      completed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Completed' },
      passed_back: { bg: 'bg-red-100', text: 'text-red-800', label: 'Reassigned' },
      cancelled: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Cancelled' },
      draft: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Draft' }
    };
    return badges[status] || badges.posted;
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const badges = {
      unfunded: { bg: 'bg-red-100', text: 'text-red-800', label: 'Unfunded' },
      funded: { bg: 'bg-green-100', text: 'text-green-800', label: 'Funded' },
      held: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Held' },
      released: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Released' },
      refunded: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Refunded' },
      refund_pending: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Refund Pending' }
    };
    return badges[paymentStatus] || badges.unfunded;
  };

  const validActions = getValidActions();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/admin')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Jobs Management</h1>
                <p className="text-sm text-gray-600">Bulk actions and job administration</p>
              </div>
            </div>
            
            <button
              onClick={fetchJobs}
              className="jop-button-secondary flex items-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Bulk Toolbar - Visible when jobs selected */}
        {selectedJobs.size > 0 && (
          <div className="jop-card p-4 mb-6 border-l-4 border-blue-500 bg-blue-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-blue-900">
                  Selected: {selectedJobs.size} job{selectedJobs.size === 1 ? '' : 's'}
                </span>
                
                <div className="flex space-x-2">
                  {validActions.map(action => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={action.id}
                        onClick={() => setShowBulkConfirmation(action)}
                        disabled={bulkLoading}
                        className={`flex items-center px-3 py-1 rounded-md border text-sm font-medium transition-colors ${action.color} border-current hover:bg-current hover:text-white`}
                      >
                        <Icon className="h-4 w-4 mr-1" />
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <button
                onClick={() => {
                  setSelectedJobs(new Set());
                  setAllSelected(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="jop-card p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({...prev, status: e.target.value}))}
                className="jop-select"
              >
                <option value="all">All Statuses</option>
                <option value="posted">Live</option>
                <option value="accepted">Assigned</option>
                <option value="completed">Completed</option>
                <option value="passed_back">Reassigned</option>
                <option value="cancelled">Cancelled</option>
                <option value="draft">Draft</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
              <select
                value={filters.payment_status}
                onChange={(e) => setFilters(prev => ({...prev, payment_status: e.target.value}))}
                className="jop-select"
              >
                <option value="all">All Payment Status</option>
                <option value="unfunded">Unfunded</option>
                <option value="funded">Funded</option>
                <option value="held">Held</option>
                <option value="released">Released</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({...prev, search: e.target.value}))}
                  className="jop-input pl-10"
                  placeholder="Search jobs, locations, businesses..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Jobs Table */}
        <div className="jop-card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Jobs ({jobs.length})</h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <button
                      onClick={handleSelectAll}
                      className="flex items-center text-gray-500 hover:text-gray-700"
                    >
                      {allSelected ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                    </button>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Payment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Originator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobs.map((job) => {
                  const statusBadge = getStatusBadge(job.status);
                  const paymentBadge = getPaymentStatusBadge(job.payment_status || 'unfunded');
                  
                  return (
                    <tr 
                      key={job.id} 
                      className={`hover:bg-gray-50 ${selectedJobs.has(job.id) ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleSelectJob(job.id)}
                          className="flex items-center text-gray-500 hover:text-gray-700"
                        >
                          {selectedJobs.has(job.id) ? (
                            <CheckSquare className="h-4 w-4 text-blue-600" />
                          ) : (
                            <Square className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs">
                          {job.title || 'Untitled Job'}
                        </div>
                        <div className="text-xs text-gray-500">
                          JOP-{job.id.slice(0, 8).toUpperCase()}
                        </div>
                        <div className="text-xs text-gray-600 truncate max-w-xs">
                          {job.location}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusBadge.bg} ${statusBadge.text}`}>
                          {statusBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${paymentBadge.bg} ${paymentBadge.text}`}>
                          {paymentBadge.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {job.originator_business || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          ${job.quoted_price || 0}
                        </div>
                        {job.receiver_amount && (
                          <div className="text-xs text-gray-500">
                            Net: ${job.receiver_amount}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {new Date(job.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/jobs/${job.id}`)}
                          className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            
            {jobs.length === 0 && (
              <div className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No jobs found</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Try adjusting your filters to see more jobs.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bulk Confirmation Modal */}
      {showBulkConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <AlertTriangle className="h-6 w-6 text-orange-600 mr-2" />
                    Confirm Bulk Action
                  </h3>
                </div>
                <button
                  onClick={() => setShowBulkConfirmation(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-6">
                {showBulkConfirmation.id === 'cancel' && (
                  <p className="text-gray-700">
                    Cancel <strong>{showBulkConfirmation.validCount}</strong> job{showBulkConfirmation.validCount === 1 ? '' : 's'}? 
                    These will be removed from boards and receivers will be notified. This action cannot be undone.
                  </p>
                )}
                
                {showBulkConfirmation.id === 'refund_cancel' && (
                  <p className="text-gray-700">
                    Refund & cancel <strong>{showBulkConfirmation.validCount}</strong> job{showBulkConfirmation.validCount === 1 ? '' : 's'}? 
                    Escrow will be refunded (mock mode). Proceed?
                  </p>
                )}
                
                {showBulkConfirmation.id === 'delete_drafts' && (
                  <p className="text-gray-700">
                    Permanently delete <strong>{showBulkConfirmation.validCount}</strong> draft job{showBulkConfirmation.validCount === 1 ? '' : 's'}? 
                    This cannot be undone.
                  </p>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => handleBulkAction(showBulkConfirmation.id)}
                  disabled={bulkLoading}
                  className="jop-button flex-1 flex items-center justify-center bg-red-600 hover:bg-red-700"
                >
                  {bulkLoading ? (
                    <div className="spinner" />
                  ) : (
                    <>
                      <showBulkConfirmation.icon className="h-4 w-4 mr-2" />
                      Confirm {showBulkConfirmation.label.split(' (')[0]}
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowBulkConfirmation(null)}
                  className="jop-button-secondary"
                  disabled={bulkLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Results Modal */}
      {bulkResults && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-lg">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Bulk Action Results</h3>
                <button
                  onClick={() => setBulkResults(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="mb-4 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-600">{bulkResults.summary.succeeded}</div>
                  <div className="text-xs text-gray-600">Succeeded</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-yellow-600">{bulkResults.summary.skipped}</div>
                  <div className="text-xs text-gray-600">Skipped</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-red-600">{bulkResults.summary.failed}</div>
                  <div className="text-xs text-gray-600">Failed</div>
                </div>
              </div>

              <div className="max-h-60 overflow-y-auto">
                {bulkResults.results.map((result, index) => (
                  <div 
                    key={`${result.job_id}-${index}`}
                    className={`p-2 rounded mb-2 text-sm ${
                      result.status === 'success' ? 'bg-green-50 text-green-800' :
                      result.status === 'skipped' ? 'bg-yellow-50 text-yellow-800' :
                      'bg-red-50 text-red-800'
                    }`}
                  >
                    <div className="font-medium">
                      JOP-{result.job_id.slice(0, 8).toUpperCase()}
                    </div>
                    <div className="text-xs">
                      {result.status.toUpperCase()}: {result.reason}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4">
                <button
                  onClick={() => setBulkResults(null)}
                  className="jop-button w-full"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminJobsManagement;